import * as THREE from "three";
import { inked, toon } from "../toon";
import { facets } from "./geom";
import { HAIR_SWING } from "./long-hair";

// A ponytail (#234, Maddy's), head space, face +z: the hair pulled back over
// the skull to a tie high on the back of the head, and the tail hanging from
// the tie in its own `hair-swing` group, pivoting on the tie, so `HairSwing`
// (`hair-swing.ts`) swings it the way it swings long hair's fall, clamped out
// over the back. The tail's line and thickness are shared; the toy draws it
// as one tube (here), the natural face as a core with strands over it
// (`head-natural.ts`'s `buildNaturalPonytail`).

/** Where the toy's tail is tied: high on the back of the head, just off the hair (head space). */
export const TOY_PONYTAIL_TIE: Readonly<THREE.Vector3> = new THREE.Vector3(0, 0.1, -0.205);

/**
 * The tail's centre line from the tie (the swing group's origin): out
 * behind the head, then down past the nape to just above the shoulders,
 * standing clear of the back of the skull and the neck, and ending above
 * the backpack's flap (0.48 up the torso) however the head nods.
 */
export const PONYTAIL_LINE: readonly THREE.Vector3[] = [
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(0, -0.025, -0.05),
  new THREE.Vector3(0, -0.11, -0.08),
  new THREE.Vector3(0, -0.22, -0.085),
  new THREE.Vector3(0, -0.31, -0.075),
];

/** The tail's thickness `t` of the way along it: gathered at the tie, full a third of the way, a soft point at the tip. */
export function ponytailRadius(t: number): number {
  const gather = Math.min(1, t / 0.28);
  const body = 0.026 + 0.03 * Math.sin((gather * Math.PI) / 2);
  return t <= 0.28 ? body : 0.056 * (1 - 0.82 * ((t - 0.28) / 0.72) ** 1.4);
}

/** The tail's centre line as a curve. */
export function ponytailCurve(): THREE.CatmullRomCurve3 {
  return new THREE.CatmullRomCurve3(PONYTAIL_LINE.map((p) => p.clone()));
}

/**
 * The tail as a closed tube along `PONYTAIL_LINE`, `scale` times
 * `ponytailRadius` thick, flattened a little side to side, closed at the tip.
 */
export function ponytailGeometry(scale = 1, along = 16, around = 12): THREE.BufferGeometry {
  const curve = ponytailCurve();
  const segs = facets(along, 4);
  const sides = facets(around, 3);
  const { normals, binormals } = curve.computeFrenetFrames(segs, false);
  const pos: number[] = [];
  const uv: number[] = [];
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    curve.getPointAt(t, p);
    const r = i === segs ? 1e-4 : ponytailRadius(t) * scale;
    for (let k = 0; k <= sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      // Normal (in the tail's plane, front to back) full; binormal (side to side) a little narrower.
      n.copy(normals[i]!)
        .multiplyScalar(Math.cos(a))
        .addScaledVector(binormals[i]!, Math.sin(a) * 0.85);
      pos.push(p.x + n.x * r, p.y + n.y * r, p.z + n.z * r);
      uv.push(k / sides, 1 - t);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < segs; i++) {
    for (let k = 0; k < sides; k++) {
      const a = i * (sides + 1) + k;
      const b = a + sides + 1;
      index.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

/** The scrunchie round the tie: a soft ring facing down the tail's first stretch. */
export function buildHairTie(at: THREE.Vector3, radius = 0.032): THREE.Mesh {
  const band = inked(new THREE.TorusGeometry(radius, 0.012, facets(6, 2), facets(16)), toon("#2a2a33", { roughness: 0.7 }, "cloth"), 0.008);
  band.name = "hair-tie";
  band.position.copy(at);
  const first = PONYTAIL_LINE[1]!.clone().sub(PONYTAIL_LINE[0]!).normalize();
  band.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), first);
  return band;
}

/** The tail in its own group (`hair-swing`) at the tie, so it swings from there. */
export function buildTail(tie: THREE.Vector3, parts: THREE.Object3D[]): THREE.Group {
  const swing = new THREE.Group();
  swing.name = HAIR_SWING;
  swing.position.copy(tie);
  swing.add(...parts);
  return swing;
}

/**
 * The toy's ponytail: the hair pulled back smooth over the skull, a
 * side-swept fringe, a gathered knot and scrunchie at the tie, and the
 * tail hanging from it.
 */
export function buildToyPonytail(color: string): THREE.Group {
  const hair = new THREE.Group();
  hair.name = "ponytail-hair";
  const mat = toon(color, { roughness: 0.6 }, "hair");
  // Pulled back: the cap sits close, tipped back so it covers the crown to the nape.
  const cap = inked(new THREE.SphereGeometry(0.208, facets(24), facets(10, 2), 0, Math.PI * 2, 0, Math.PI * 0.5), mat, 0.015);
  cap.name = "hair";
  cap.scale.set(1.02, 1.03, 1.03);
  cap.rotation.x = -0.36;
  hair.add(cap);
  // The fringe: a slimmer roll than long hair's, swept off the forehead.
  const fringe = inked(new THREE.CapsuleGeometry(0.032, 0.18, facets(4, 1), facets(10)), mat, 0.01);
  fringe.name = "fringe";
  fringe.rotation.set(0.15, 0, Math.PI / 2 - 0.3);
  fringe.scale.set(1, 1, 0.6);
  fringe.position.set(0.01, 0.16, 0.15);
  hair.add(fringe);
  // The knot the hair is gathered into at the tie.
  const knot = inked(new THREE.SphereGeometry(0.04, facets(12), facets(8, 2)), mat, 0.01);
  knot.name = "knot";
  knot.scale.set(1, 0.9, 0.8);
  knot.position.copy(TOY_PONYTAIL_TIE).add(new THREE.Vector3(0, 0, 0.012));
  hair.add(knot);
  hair.add(buildHairTie(TOY_PONYTAIL_TIE.clone().add(new THREE.Vector3(0, -0.006, -0.012))));
  const tail = inked(ponytailGeometry(), mat, 0.01);
  tail.name = "ponytail";
  hair.add(buildTail(TOY_PONYTAIL_TIE.clone(), [tail]));
  return hair;
}
